import { Injectable } from '@angular/core';
import {AuthHttp} from '../../../user/services/auth/http/auth-http.service';
import {ObjectMapService} from '../../../utilities/services/object-map/object-map.service';
import {PaginationAbstractService} from '../../../utilities/services/pagination-abstract/pagination-abstract.service';
import {CerfParams} from '../../interfaces/CerfParams';
import {Cerf} from '../../interfaces/Cerf';
import {environment} from '../../../../../environments/environment';
import {Pagination} from '../../../utilities/interfaces/Pagination';
import 'rxjs/add/operator/map';
import {CerfComment} from '../../interfaces/CerfComment';

@Injectable()
export class CerfService extends PaginationAbstractService<Cerf, CerfParams> {

  private route = 'cerf';

  constructor(private http: AuthHttp, private objMapService: ObjectMapService) {
    super(objMapService, 'cerfParams');
  }

  public getCerfs(params?: CerfParams): Promise<Pagination<Cerf>> {
    return this.getPagination(params).toPromise();
  }

  protected prefetch() {
    return this.http
      .get(`${environment.baseUrl}${this.route}`, {params: this._params})
      .map((res) => res.json());
  }

  public getCerf(id) {
    return this.http.get(`${environment.baseUrl}${this.route}/${id}`).map((res) => res.json()).toPromise();
  }

  public async create(cerf, files) {
    this.clearCache();
    const cerfFormData = this.createCerfFormData(cerf, files);
    return this.http.post(`${environment.baseUrl}${this.route}`, cerfFormData).toPromise();
  }

  public update(cerf, files, deletedFiles, cerfId) {
    this.clearCache();
    const cerfFormData = this.createCerfFormData(cerf, files);
    if (deletedFiles.length > 0) cerfFormData.append('deletedFiles', JSON.stringify(deletedFiles));
    return this.http.post(`${environment.baseUrl}${this.route}/${cerfId}`, cerfFormData).map((res) => res.json()).toPromise();
  }

  private stripDollarAndCommas(value) {
    return value.replace('$', '').replace(/,/g, '');
  }

  private sanitizeCurrencyValues(cerf) {
    cerf.basic_costs = this.stripDollarAndCommas(cerf.basic_costs);
    cerf.other_costs = this.stripDollarAndCommas(cerf.other_costs);
    cerf.freight_costs = this.stripDollarAndCommas(cerf.freight_costs);
    cerf.taxes = this.stripDollarAndCommas(cerf.taxes);
    cerf.total_costs = this.stripDollarAndCommas(cerf.total_costs);
    cerf.january_march_expenditure_costs = this.stripDollarAndCommas(cerf.january_march_expenditure_costs);
    cerf.april_june_expenditure_costs = this.stripDollarAndCommas(cerf.april_june_expenditure_costs);
    cerf.july_september_expenditure_costs = this.stripDollarAndCommas(cerf.july_september_expenditure_costs);
    cerf.october_december_expenditure_costs = this.stripDollarAndCommas(cerf.october_december_expenditure_costs);
    cerf.next_year_expenditure_costs = this.stripDollarAndCommas(cerf.next_year_expenditure_costs);
    cerf.total_expenditure_costs = this.stripDollarAndCommas(cerf.total_expenditure_costs);
  }

  private createCerfFormData = (cerf, files) => {
    this.sanitizeCurrencyValues(cerf);
    const cerfFormData = new FormData();
    cerfFormData.append('cerf', JSON.stringify(cerf));
    for (const file of files) {
      cerfFormData.append(file.name, file);
    }
    return cerfFormData;
  };

  public getFileUrl(cerfId: number, filename: string) {
    return `${environment.baseUrl}${this.route}/${cerfId}/file/${encodeURIComponent(filename)}?token=${this.http.getTokenBearer()}`;
  }

  public downloadPdfUrl(cerfId: number) {
    return encodeURI(`${environment.baseUrl}${this.route}/${cerfId}/download-pdf?token=${this.http.getTokenBearer()}`);
  }

  public addComment(cerfId: number, text: string): Promise<CerfComment> {
    return this.http.post(`${environment.baseUrl}${this.route}/${cerfId}/comments`,
      {text: text}, {throwError: true}).map((res) => res.json()).toPromise();
  }

  public approveCerfRequest(cerfId) {
    this.clearCache();
    return this.http.post(`${environment.baseUrl}${this.route}/${cerfId}/approve`, {}).toPromise();
  }

  public declineCerfRequest(cerfId) {
    this.clearCache();
    return this.http.post(`${environment.baseUrl}${this.route}/${cerfId}/decline`, {}).toPromise();
  }

  public runReport(filters) {
    return this.http.post(`${environment.baseUrl}${this.route}/run-report`,
      {filters: filters}).map(r => r.text())
      .do((name) =>
        window.location.href =
        encodeURI(`${environment.baseUrl}${this.route}/get-report?token=${this.http.getTokenBearer()}&file=${name}`)
      )
      .toPromise();
  }

}



// WEBPACK FOOTER //
// /var/www/dnata-cerf-front-end/releases/20200326161233/src/app/modules/cerf/services/cerf/cerf.service.ts